import { useEffect, useState, useCallback } from "react";
import { SeoHead } from "@/components/SeoHead";
import { Link } from "wouter";
import { X } from "lucide-react";

interface TipProps {
  number: number;
  badge?: string;
  title: string;
  description: string;
  steps: string[];
  children?: React.ReactNode;
  note?: string;
}

function TipCard({ number, badge, title, description, steps, children, note }: TipProps) {
  return (
    <section className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-5 border-b border-border bg-[#f4faf6]">
        <span className="flex-shrink-0 w-9 h-9 rounded-full bg-primary text-white text-base font-bold flex items-center justify-center">
          {number}
        </span>
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          {badge && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium border border-primary/20">
              {badge}
            </span>
          )}
        </div>
      </div>
      <div className="px-6 py-6 space-y-5">
        <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
        <ol className="space-y-2">
          {steps.map((step, i) => (
            <li key={i} className="flex items-start gap-3 text-sm">
              <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center">
                {i + 1}
              </span>
              <span className="text-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: step }} />
            </li>
          ))}
        </ol>
        {note && (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
            <span className="mt-0.5 flex-shrink-0">!</span>
            <span dangerouslySetInnerHTML={{ __html: note }} />
          </div>
        )}
        {children}
      </div>
    </section>
  );
}

interface LightboxProps {
  src: string;
  alt: string;
  onClose: () => void;
}

function Lightbox({ src, alt, onClose }: LightboxProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white bg-black/40 hover:bg-black/60 rounded-full p-2 transition-colors"
        onClick={onClose}
        aria-label="關閉"
      >
        <X className="h-6 w-6" />
      </button>
      <img
        src={src}
        alt={alt}
        className="max-h-[90vh] max-w-[92vw] rounded-xl shadow-2xl object-contain"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

export default function TipsPage() {
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const open = useCallback((src: string, alt: string) => setLightbox({ src, alt }), []);
  const close = useCallback(() => setLightbox(null), []);

  const Img = ({ src, alt, className }: { src: string; alt: string; className: string }) => (
    <img
      src={src}
      alt={alt}
      className={`${className} cursor-zoom-in`}
      onClick={() => open(src, alt)}
    />
  );

  return (
    <>
      <SeoHead
        title="使用貼士｜Skoltend 香港學校招標資訊平台"
        description="Skoltend 使用貼士：加入主畫面、訂閱 Telegram 頻道、精準篩選招標、設定通知電郵、瀏覽每週精選等實用指引。"
        canonical="https://www.skoltend.com/tips"
      />
      {lightbox && <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={close} />}
      <div className="min-h-screen bg-[#f4faf6]">
        <div className="bg-primary text-white py-12">
          <div className="container mx-auto px-4 md:px-8 max-w-4xl">
            <h1 className="text-3xl font-bold mb-2">溫馨提示</h1>
            <p className="text-primary-foreground/80 text-sm">充分利用 Skoltend 的各項功能，輕鬆掌握每日學校招標商機</p>
          </div>
        </div>

        <div className="container mx-auto px-4 md:px-8 max-w-4xl py-10 space-y-8">

          {/* Tip 1 — 加入主畫面 */}
          <TipCard
            number={1}
            title="一鍵加入主畫面"
            description="將 Skoltend 加入手機主畫面，即可像使用 App 般快速開啟，方便隨時查閱最新招標資訊。"
            steps={[
              "在 <strong>Safari</strong> 瀏覽器開啟 <strong>skoltend.com</strong>",
              "點擊底部工具列的「<strong>共享</strong>」按鈕（方框加箭頭圖示）",
              "在選單中向下滑動，點選「<strong>加至主畫面</strong>」",
              "點擊右上角「<strong>加入</strong>」完成",
            ]}
            note="此功能需使用 <strong>Safari</strong> 瀏覽器，Chrome 及 Firefox 不支援 iOS 加至主畫面。"
          >
            <div className="mt-4">
              <p className="text-xs text-muted-foreground font-medium mb-3 uppercase tracking-wide">操作步驟示範</p>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {[
                  { src: "/tips/safari1.jpeg", caption: "步驟 1：點擊「共享」" },
                  { src: "/tips/safari2.jpeg", caption: "步驟 2：選「加至主畫面」" },
                  { src: "/tips/safari3.jpeg", caption: "步驟 3：加入成功" },
                ].map(({ src, caption }) => (
                  <div key={src} className="flex-shrink-0 flex flex-col items-center gap-2">
                    <Img
                      src={src}
                      alt={caption}
                      className="h-[432px] w-auto rounded-xl border border-border shadow-sm object-cover"
                    />
                    <span className="text-xs text-muted-foreground text-center">{caption}</span>
                  </div>
                ))}
              </div>
            </div>
          </TipCard>

          {/* Tip 2 — Telegram 頻道 */}
          <TipCard
            number={2}
            title="加入 Telegram 招標頻道"
            description="訂閱免費的 Skoltend Telegram 頻道，每日被動接收最新學校招標通知，無需主動查看網頁。"
            steps={[
              "登入後前往「<strong>控制台</strong>」",
              "找到「<strong>Telegram 頻道</strong>」區域，掃描 QR Code 或點擊「<strong>加入</strong>」按鈕",
              "或直接前往 <a href='https://t.me/Skoltend' target='_blank' rel='noopener noreferrer' class='text-primary underline underline-offset-2'>t.me/Skoltend</a>，點擊「<strong>View in Telegram</strong>」加入頻道",
              "加入後每日將收到最新招標分類通知",
            ]}
          >
            <div className="mt-4 space-y-4">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">加入方法</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <Img
                    src="/tips/tg-dashboard.jpeg"
                    alt="控制台 Telegram 頻道區域"
                    className="w-full rounded-xl border border-border shadow-sm object-cover"
                  />
                  <span className="text-xs text-muted-foreground text-center">控制台掃 QR Code 或點「加入」</span>
                </div>
                <div className="flex flex-col gap-2">
                  <Img
                    src="/tips/tg-channel.jpeg"
                    alt="Telegram 頻道頁面"
                    className="w-full rounded-xl border border-border shadow-sm object-cover"
                  />
                  <span className="text-xs text-muted-foreground text-center">或直接訪問 t.me/Skoltend</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mt-2">頻道通知樣本</p>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {[
                  { src: "/tips/tg-sample1.png", caption: "招標列表通知" },
                  { src: "/tips/tg-sample2.png", caption: "分類快速連結" },
                ].map(({ src, caption }) => (
                  <div key={src} className="flex-shrink-0 flex flex-col items-center gap-2">
                    <Img
                      src={src}
                      alt={caption}
                      className="h-[480px] w-auto rounded-xl border border-border shadow-sm object-cover"
                    />
                    <span className="text-xs text-muted-foreground text-center">{caption}</span>
                  </div>
                ))}
              </div>
            </div>
          </TipCard>

          {/* Tip 3 — WhatsApp 社群 */}
          <TipCard
            number={3}
            title="加入 WhatsApp 社群"
            description="加入免費的 Skoltend WhatsApp 公告社群，每日接收最新學校招標資訊。社群只供管理員發佈消息，不會被其他會員的訊息打擾。"
            steps={[
              "登入後前往「<strong>控制台</strong>」",
              "找到「<strong>WhatsApp 社群</strong>」區域，掃描 QR Code 或點擊「<strong>加入</strong>」按鈕",
              "在 WhatsApp 開啟邀請連結後，點擊「<strong>加入社群</strong>」完成加入",
              "加入後每日即可在 WhatsApp 接收最新招標公告及分類資訊",
            ]}
          >
            <div className="mt-4 space-y-4">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">加入方法</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <Img
                    src="/tips/whatsapp-dashboard.png"
                    alt="控制台 WhatsApp 社群區域"
                    className="w-full rounded-xl border border-border shadow-sm object-cover"
                  />
                  <span className="text-xs text-muted-foreground text-center">控制台掃 QR Code 或點「加入」</span>
                </div>
                <div className="flex flex-col gap-2">
                  <Img
                    src="/tips/whatsapp-sample.png"
                    alt="WhatsApp 社群招標公告樣本"
                    className="w-full rounded-xl border border-border shadow-sm object-cover"
                  />
                  <span className="text-xs text-muted-foreground text-center">加入後接收每日招標公告及分類資訊</span>
                </div>
              </div>
            </div>
          </TipCard>

          {/* Tip 4 — 篩選搜尋 */}
          <TipCard
            number={4}
            title="精準篩選及關鍵字搜尋"
            description="「全部招標」頁面提供多維度篩選，配合關鍵字搜尋快速定位目標招標。篩選條件會自動儲存至帳戶，下次登入自動套用。"
            steps={[
              "點擊導航欄「<strong>全部招標</strong>」",
              "依需要選擇「<strong>狀態</strong>」、「<strong>時間</strong>」、「<strong>地區</strong>」及「<strong>分類</strong>」",
              "在「<strong>搜尋</strong>」欄輸入關鍵字（如「電腦」、「午膳」、「清潔」）進一步精準篩選",
              "篩選完成後，系統會顯示「設定已同步至帳戶」——下次登入自動套用相同設定",
            ]}
          >
            <div className="mt-4 space-y-4">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">操作示範</p>
              <div className="space-y-3">
                <div className="flex flex-col gap-2">
                  <Img
                    src="/tips/tender1.jpeg"
                    alt="全部招標篩選頁面"
                    className="w-full rounded-xl border border-border shadow-sm object-cover"
                  />
                  <span className="text-xs text-muted-foreground text-center">選擇地區、分類等篩選條件</span>
                </div>
                <div className="flex flex-col gap-2">
                  <Img
                    src="/tips/tender2.jpeg"
                    alt="關鍵字搜尋示範"
                    className="w-full rounded-xl border border-border shadow-sm object-cover"
                  />
                  <span className="text-xs text-muted-foreground text-center">輸入關鍵字「電腦」精準篩選結果，系統自動記存設定</span>
                </div>
              </div>
            </div>
          </TipCard>

          {/* Tip 5 — Apple ID 通知電郵 */}
          <TipCard
            number={5}
            title="Apple ID 用戶：設定通知電郵地址"
            description="使用 Apple ID 登入且選擇了「隱藏我的電郵」的會員，建議更改通知電郵地址為常用信箱，確保每日招標通知準時送達。"
            steps={[
              "登入後前往「<strong>控制台</strong>」",
              "找到「<strong>電郵通知設定</strong>」區域，查看目前的「通知電郵地址」",
              "如顯示 <code class='text-xs bg-muted px-1 rounded'>@privaterelay.appleid.com</code> 格式，點擊右側「<strong>修改</strong>」",
              "輸入常用電郵地址（如 Gmail、Outlook）並儲存",
            ]}
            note="Apple private relay 電郵（@privaterelay.appleid.com）有機會攔截來自第三方服務的郵件，導致收不到每日招標通知。更改為常用電郵可確保穩定收件。<br/><strong>注意：</strong>只有以 Apple ID 登入的帳戶才能修改通知電郵地址。"
          >
            <div className="mt-4 space-y-2">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">設定位置</p>
              <Img
                src="/tips/apple-email.jpeg"
                alt="電郵通知設定"
                className="w-full rounded-xl border border-border shadow-sm object-cover"
              />
              <span className="block text-xs text-muted-foreground text-center">控制台「電郵通知設定」→ 點擊「修改」更改通知電郵地址</span>
            </div>
          </TipCard>

          {/* Tip 6 — 每週精選 */}
          <TipCard
            number={6}
            badge="付費會員"
            title="每週精選：查看真實招標完整資訊"
            description="「每週精選」每週從全港 1,000+ 所學校中隨機抽選 5 個真實招標案例，讓您感受付費會員所看到的完整資訊，包括學校名稱、聯絡人、電話及地址。"
            steps={[
              "點擊導航欄「<strong>每週精選</strong>」",
              "瀏覽本週精選的 5 個真實學校招標案例",
              "付費會員可看到完整資訊：學校名稱、聯絡人、電話號碼、學校地址及招標文件連結",
              "免費會員可點擊「<strong>了解付費計劃</strong>」升級以查看全部招標的完整資料",
            ]}
          >
            <div className="mt-4 space-y-2">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">每週精選示範</p>
              <Img
                src="/tips/weekly.jpeg"
                alt="每週精選頁面示範"
                className="w-full rounded-xl border border-border shadow-sm object-cover"
              />
              <span className="block text-xs text-muted-foreground text-center">付費會員可看到完整學校資訊、聯絡人及招標文件連結</span>
            </div>
          </TipCard>

          {/* CTA */}
          <div className="rounded-2xl bg-primary text-white px-8 py-8 text-center space-y-4">
            <h3 className="text-xl font-bold">立即體驗 Skoltend</h3>
            <p className="text-primary-foreground/80 text-sm">免費註冊，每日掌握全港學校招標商機</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link href="/tenders">
                <button className="px-5 py-2 bg-white text-primary font-semibold rounded-lg text-sm hover:bg-white/90 transition-colors">
                  瀏覽全部招標
                </button>
              </Link>
              <Link href="/pricing">
                <button className="px-5 py-2 bg-white/10 border border-white/30 text-white font-semibold rounded-lg text-sm hover:bg-white/20 transition-colors">
                  了解付費計劃
                </button>
              </Link>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
